<template>
  <div class="comments-wrapper">
    <!-- Comments List -->
    <div v-if="commentsDataArray.length > 0" class="comments-list">
      <div
        v-for="comment in commentsDataArray"
        :key="comment.id"
        class="comment-item"
        :class="{ 'comment-admin': comment.type === 'Admin' }"
      >
        <div class="comment-avatar">
          <img
            v-if="comment.userAvatar && comment.userAvatar !== ''"
            :src="getImgUrl(comment.userAvatar)"
            alt=""
          />
          <div v-else class="avatar-placeholder">
            {{ getInitials(comment.name) }}
          </div>
        </div>
        <div class="comment-content">
          <div class="comment-header">
            <span class="comment-author">{{ comment.name }}</span>
            <span v-if="comment.type === 'Admin'" class="comment-role-badge">Coach</span>
            <span v-if="comment.date" class="comment-time">{{ formatCommentDate(comment.date) }}</span>
          </div>
          <div class="comment-body">
            <img
              v-if="comment.userImage && comment.userImage !== ''"
              :src="getImgUrl(comment.userImage)"
              alt=""
              class="comment-image"
            />
            <div class="comment-text" v-html="comment.comment" />
          </div>
          <button
            v-if="canDeleteComment(comment)"
            class="comment-delete-btn"
            @click="deleteComment(comment)"
            title="Delete comment"
          >
            <i class="bi bi-trash3"></i>
          </button>
        </div>
      </div>
    </div>
    <div v-else class="no-comments">
      <i class="bi bi-chat-left-text"></i>
      <p>No comments yet. Be the first to add one.</p>
    </div>

    <!-- Comment Input -->
    <div class="comment-input-area">
      <div class="editor-row">
        <div class="editor-wrap">
          <QuillEditor
            v-model:content="selectedCommentData.comment"
            contentType="html"
            theme="snow"
            placeholder="Write a comment..."
          />
        </div>
        <button class="attach-btn" @click="showUploadImage = true" title="Attach image">
          <i class="bi bi-paperclip"></i>
        </button>
      </div>
      <div class="input-actions">
        <button class="send-btn" @click="addAcomment()" :disabled="!hasCommentContent">
          <i class="bi bi-send"></i> Send
        </button>
      </div>
    </div>

    <!-- Upload Image Modal -->
    <div v-if="showUploadImage" class="modal-overlay" @click.self="showUploadImage = false">
      <div class="modal-dialog" @click.stop>
        <div class="modal-content upload-modal-content">
          <div class="modal-header">
            <h3><i class="bi bi-image"></i> Upload Image</h3>
            <button @click="showUploadImage = false" class="close-button">&times;</button>
          </div>
          <div class="modal-body">
            <ImageUploader
              @populateImageObjectFromUpload="imageUploadFileDataChnged($event)"
              @populateImageFileDataFromUpload="imageUploadObjectDataChanged($event)"
              @clearImageDataFromUpload="clearImageDataFromUpload()"
              :imageURL="selectedCommentData.userImage"
            />
            <div class="upload-editor-wrap">
              <QuillEditor
                v-model:content="selectedCommentData.comment"
                contentType="html"
                theme="snow"
                placeholder="Add a caption..."
              />
            </div>
            <div class="upload-actions">
              <button class="btn-cancel" @click="showUploadImage = false">Cancel</button>
              <button class="send-btn" @click="addAcomment()">
                <i class="bi bi-send"></i> Send with Image
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { CHECK_PROFILE_HAS_DATA } from "@/store/actions/user.js";
import { Comments } from "@/services/comments-service.js";
import { Users } from "@/services/user-service.js";
import ImageUploader from "../../components/ImageUploader";
import moment from "moment";
import { QuillEditor } from "@vueup/vue-quill";
import "@vueup/vue-quill/dist/vue-quill.snow.css";

export default {
  name: "Comments",
  components: {
    ImageUploader,
    QuillEditor,
  },
  data: function () {
    return {
      allUsers: [],
      commentsDataArray: [],
      selectedCommentData: {
        user_id: null,
        comment: "",
        date: null,
        type: "User",
        type_id: null,
        teamId: null,
        userImage: "",
      },
      userData: {
        name: "",
        userType: "",
        id: null,
      },
      imageFile: null,
      imageToBeRemoved: null,
      imageURL: "",
      image: {
        imageName: "",
        path: "",
        file: null,
      },
      showUploadImage: false,
    };
  },
  props: {
    commentsData: {
      type: Array,
      default: null,
    },
    userAndTypeIds: {
      type: Object,
      default: null,
    },
  },
  computed: {
    ...mapGetters(["getProfile"]),
    hasCommentContent() {
      if (!this.selectedCommentData.comment) return false;
      const stripped = this.selectedCommentData.comment.replace(/<[^>]*>/g, '').trim();
      return stripped.length > 0;
    },
  },
  watch: {
    commentsData: function (newValue) {
      if (newValue !== null) {
        this.getAllUsersNamesByClubId();
        this.commentsDataArray = newValue;
      }
    },
  },
  async created() {
    const response = await this.$store.dispatch(CHECK_PROFILE_HAS_DATA);
    if (response === true) {
      this.setUser();
      this.setUserAndTypeIds();
      this.getAllUsersNamesByClubId();
      this.commentsDataArray = this.commentsData;
    } else {
      const clientId = JSON.parse(
        localStorage.getItem("the_w_selectedClientId"),
      );
      this.getUserById(clientId);
    }
  },
  methods: {
    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
    },
    formatCommentDate(dateStr) {
      if (!dateStr) return '';
      const m = moment(dateStr);
      if (m.isSame(moment(), 'day')) return m.format('h:mm A');
      if (m.isSame(moment().subtract(1, 'day'), 'day')) return 'Yesterday ' + m.format('h:mm A');
      return m.format('D MMM YYYY, h:mm A');
    },
    canDeleteComment(comment) {
      if (!this.userData || !this.userData.id) return false;
      // Allow delete if user owns the comment or is admin/coach
      if (String(comment.user_id) === String(this.userData.id)) return true;
      if (this.userData.userType === 'Admin' || this.userData.userType === 'Coach' || this.userData.userType === 'HeadCoach') return true;
      return false;
    },
    async deleteComment(comment) {
      if (!confirm('Delete this comment?')) return;
      try {
        const res = await Comments.find({
          action: 'deleteComment',
          commentId: comment.id,
          user_id: this.userData.userType === 'Admin' || this.userData.userType === 'Coach' || this.userData.userType === 'HeadCoach'
            ? comment.user_id
            : this.userData.id,
        });
        if (res.data && res.data.success) {
          this.$emit("getComments", this.selectedCommentData.type_id);
        }
      } catch (err) {
        console.error('Error deleting comment:', err);
      }
    },
    clearImageDataFromUpload() {
      this.imageURL = "";
      this.image = {
        imageName: "",
        path: "",
        file: null,
      };
    },
    imageUploadObjectDataChanged(data) {
      this.image = data;
    },
    imageUploadFileDataChnged(data) {
      this.imageFile = data;
    },
    getImgUrl(pic) {
      return pic !== undefined && pic !== "" ? `/images/${pic}` : "";
    },
    addUserDetailsToEachComment() {
      const newArray = this.commentsData.map((commentData) => {
        const userData = this.allUsers.filter((user) => {
          return commentData.user_id == user.id;
        });

        return {
          ...commentData,
          name: userData[0] ? userData[0].name : 'Unknown',
          userAvatar: userData[0] ? userData[0].userImage : '',
        };
      });

      this.commentsDataArray = newArray;
    },
    async getAllUsersNamesByClubId() {
      this.allUsers = [];
      const userData = {
        clubId: this.userAndTypeIds.clubId,
        action: "getAllUsersNamesByClubId",
      };
      const responce = await Users.find(userData).catch((error) => {
        console.log(error);
      });
      if (responce.data.length > 0) {
        this.allUsers = responce.data;
        this.addUserDetailsToEachComment();
      }
    },
    populateForAdminComment() {
      if (this.userData.userType === "Admin") {
        this.selectedCommentData.type = "Admin";
        this.selectedCommentData.user_id = this.userData.id;
      } else {
        this.setUserAndTypeIds();
      }
    },
    setUserAndTypeIds() {
      this.selectedCommentData.user_id = this.userAndTypeIds.user_id;
      this.selectedCommentData.type_id = this.userAndTypeIds.type_id;
      this.selectedCommentData.teamId = this.userAndTypeIds.teamId;
    },
    async addAcomment() {
      if (!this.hasCommentContent && !this.imageFile) return;
      this.selectedCommentData.date = this.changeTimeToTimeAndDate(
        moment().format("HH:mm"),
        moment().format("YYYY-MM-DD"),
      );
      this.populateForAdminComment();
      if (this.image.imageFile !== null && this.imageFile !== null) {
        this.selectedCommentData.fileName = this.imageFile.imageName;
        this.selectedCommentData.imageFile = this.image;
      }
      const userData = {
        ...this.selectedCommentData,
        action: "saveComment",
      };
      const responce = await Comments.find(userData).catch((error) => {
        console.log(error);
      });

      if (responce.data.length > 0) {
        this.selectedCommentData.comment = "";
        this.$emit("getComments", this.selectedCommentData.type_id);
        this.showUploadImage = false;
      }
    },
    async getUserById(clientId) {
      const userData = {
        clientId: clientId,
        action: "getUserByID",
      };
      const responce = await Users.find(userData).catch((error) => {
        console.log(error);
      });
      if (responce.data.length > 0) {
        this.userData = responce.data[0];
        this.setUserAndTypeIds();
        this.commentsDataArray = this.commentsData;
      }
    },
    setUser() {
      if (this.getProfile !== null) {
        this.userData = { ...this.getProfile };
      }
    },
    formatISOStringDateAndTime(timeDateString) {
      return moment(timeDateString).utc().format("h:mm a");
    },
    changeTimeToTimeAndDate(timeString, date) {
      return `${date}T${timeString}:00.000Z`;
    },
  },
};
</script>

<style scoped lang="scss">
.comments-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* ---- Comments List ---- */
.comments-list {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.comment-item {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  position: relative;

  &:hover .comment-delete-btn {
    opacity: 1;
  }
}

/* Avatar */
.comment-avatar {
  flex-shrink: 0;

  img {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid #e5e7eb;
  }
}

.avatar-placeholder {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #667eea;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.comment-admin .avatar-placeholder {
  background: #27ae60;
}

/* Content */
.comment-content {
  flex: 1;
  min-width: 0;
  position: relative;
}

.comment-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 2px;
}

.comment-author {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1a1a2e;
}

.comment-role-badge {
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 1px 6px;
  border-radius: 4px;
  background: #e8f8ef;
  color: #27ae60;
}

.comment-time {
  font-size: 0.75rem;
  color: #9ca3af;
}

.comment-body {
  background: #f3f4f6;
  border-radius: 0 12px 12px 12px;
  padding: 8px 12px;
  display: inline-block;
  max-width: 100%;
}

.comment-admin .comment-body {
  background: #e8f8ef;
}

.comment-text {
  font-size: 0.9rem;
  color: #374151;
  line-height: 1.5;
  word-break: break-word;

  :deep(p) {
    margin: 0;
  }
}

.comment-image {
  max-width: 300px;
  width: auto;
  border-radius: 8px;
  margin-bottom: 6px;
  display: block;
}

/* Delete Button */
.comment-delete-btn {
  position: absolute;
  top: 0;
  right: 0;
  opacity: 0;
  background: none;
  border: none;
  color: #d1d5db;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  font-size: 0.8rem;
  transition: all 0.15s;

  &:hover {
    color: #ef4444;
    background: #fef2f2;
  }
}

/* Empty State */
.no-comments {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #9ca3af;

  i {
    font-size: 2rem;
    margin-bottom: 8px;
    opacity: 0.5;
  }

  p {
    font-size: 0.9rem;
    margin: 0;
  }
}

/* ---- Comment Input ---- */
.comment-input-area {
  border-top: 1px solid #e5e7eb;
  padding: 12px 16px;
  background: #fafbfc;
}

.editor-row {
  display: flex;
  gap: 8px;
  align-items: flex-end;
}

.editor-wrap {
  flex: 1;
  min-width: 0;

  :deep(.ql-container) {
    border-radius: 0 0 8px 8px;
    font-size: 0.9rem;
    min-height: 40px;
    max-height: 120px;
    overflow-y: auto;
  }

  :deep(.ql-toolbar) {
    border-radius: 8px 8px 0 0;
    padding: 4px 8px;
  }

  :deep(.ql-editor) {
    min-height: 40px;
    padding: 8px 12px;
  }

  :deep(.ql-editor.ql-blank::before) {
    font-style: normal;
    color: #9ca3af;
    font-size: 0.9rem;
  }
}

.attach-btn {
  background: none;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 10px;
  color: #6b7280;
  cursor: pointer;
  font-size: 1.1rem;
  transition: all 0.15s;
  flex-shrink: 0;

  &:hover {
    background: #f3f4f6;
    color: #374151;
    border-color: #9ca3af;
  }
}

.input-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

.send-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #667eea;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;

  &:hover:not(:disabled) {
    background: #5a6fd6;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

/* ---- Upload Modal ---- */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal-dialog {
  width: 100%;
  max-width: 600px;
}

.upload-modal-content {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
}

.upload-modal-content .modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  border-bottom: 1px solid #e5e7eb;

  h3 {
    font-size: 1.05rem;
    font-weight: 600;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    color: #1a1a2e;
  }
}

.close-button {
  background: none;
  border: none;
  font-size: 1.4rem;
  color: #9ca3af;
  cursor: pointer;
  padding: 0;
  line-height: 1;

  &:hover {
    color: #374151;
  }
}

.upload-modal-content .modal-body {
  padding: 20px;
}

.upload-editor-wrap {
  margin-top: 16px;
}

.upload-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}

.btn-cancel {
  padding: 8px 16px;
  background: #f3f4f6;
  color: #6b7280;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: #e5e7eb;
  }
}
</style>
